import Link from 'next/link'
import AppImage from '@/components/AppImage'
import type { Transformation } from '@/types/imagekit'

export type LinkGridProps = {
  columns?: number
  links: {
    image: {
      url: string
      alt?: string
      className?: string
      transforms?: Transformation
    }
    link: {
      target?: string
      title: string
      url?: string
    }
    text?: string
  }[]
}

export default function LinkGrid({ links, columns = 4 }: LinkGridProps) {
  if (!links || !links.length) {
    return null
  }

  let gridClass = ''

  if (columns == 4 && links.length >= 4) {
    gridClass = 'grid-cols-1 @screen-s-up/main:grid-cols-2 @screen-m-up/main:grid-cols-4'
  } else {
    gridClass = 'grid-cols-1 @screen-s-up/main:grid-cols-2 @screen-m-up/main:grid-cols-3'
  }

  const singleTemplateClass = `
    @[0px]/single-simple:!grid-cols-1
    @[600px]/single-simple:!grid-cols-2
  `

  return (
    <div className="component component--link-grid">
      <div className="container @[0px]/single-simple:!max-w-[var(--container-s-max-w)]">
        <div
          className={`grid ${gridClass} ${singleTemplateClass} w-full gap-y-l @screen-s-up/main:gap-x-s @screen-ms-up/main:gap-x-m`}>
          {links.map((link, index) => {
            const {
              image: {
                url: imageUrl,
                alt: imageAlt = '',
                className: imgClass = '',
                transforms: imageTransforms = {},
              },
              link: { target = '_blank', title, url },
              text,
            } = link

            const transforms = {
              ...imageTransforms,
              width: '544',
              height: '305',
            }

            const imageEl = (
              <AppImage
                path={imageUrl}
                alt={imageAlt}
                height={transforms.height}
                width={transforms.width}
                transforms={transforms}
                className={`w-full overflow-hidden rounded-sm ${imgClass}`}
                sizes="(min-width: 1920px) 544px, (min-width: 1000px) 28vw, (min-width: 600px) 45.53vw, calc(96.07vw - 20px)"
                srcSet={{
                  268: { width: 268, height: 151 },
                  452: { width: 452, height: 254 },
                  544: { width: 544, height: 305 },
                  1088: { width: 1088, height: 610 },
                }}
              />
            )

            return (
              <div key={index} className="relative flex flex-col gap-s">
                {imageUrl ? (
                  <>
                    {url ? (
                      <Link href={url} target={target}>
                        {imageEl}
                      </Link>
                    ) : (
                      imageEl
                    )}
                  </>
                ) : null}

                <div className="flex flex-col items-start gap-s">
                  {url ? (
                    <Link
                      href={url}
                      target={target}
                      className="h4 mb-0 transition-colors duration-100 ease-in-out hover:text-link">
                      {title}&nbsp;&gt;
                    </Link>
                  ) : (
                    <span className="h4 mb-0">{title}</span>
                  )}

                  {text ? (
                    <div dangerouslySetInnerHTML={{ __html: text }} className="p--small"></div>
                  ) : null}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
